import { useEffect, useImperativeHandle, useMemo, useState, type Ref } from "react";

import { Checkbox } from "@/components/ui/checkbox";
import { useFuenteDeLaDescripcion } from "@/hooks/useFuenteDeLaDescripcion";
import { useRellenarConstruccionesConIa, useResolverElRelleno } from "@/hooks/useRellenoConIa";
import { getApiErrorMessage } from "@/lib/api";
import type { CamposDeLaFila, GuardarLaPestanaInput, Informe } from "@/lib/informes";
import { formatearAjuste, formatearCifra, formatearMonto } from "@/lib/montos";
import { cn } from "@/lib/utils";

import type { ConstruccionPropuesta } from "@/lib/rellenoConIa";

import { BandaDeRellenoConIa } from "./BandaDeRellenoConIa";
import { BarraDeGuardado } from "./BarraDeGuardado";
import { DescripcionDeLasConstrucciones } from "./DescripcionDeLasConstrucciones";
import { DialogoDeConstruccion, type GuardadoDeLaConstruccion } from "./DialogoDeConstruccion";
import { FilasPropuestas } from "./FilasPropuestas";
import { VisorDeFotos } from "./FotosDeTerreno";
import { FuenteDeLaPropuesta } from "./FuenteDeLaPropuesta";
import { ListaDeConstrucciones } from "./ListaDeConstrucciones";
import { NotasDeLaPestana } from "./NotasDeLaPestana";
import {
  AvisoDeLoRellenado,
  type AvisoDelRelleno,
} from "./RellenoConIa";
import {
  formularioDesdeLaConstruccionPropuesta,
  resumenDeLoPropuestoEnConstrucciones,
  yaEstaEnElCuadroDeConstrucciones,
} from "./construccionesConIa";
import { camposDesdeConstruccion } from "./construcciones";
import {
  filasDelCuadro,
  modoDelTerreno,
  seccionDeConstrucciones,
  type FilaDelCuadro,
} from "./cuadroDeConstrucciones";
import { EncabezadoNumerado } from "./EncabezadoNumerado";
import { fotosDelVisor } from "./registroDeTerreno";
import type { EstadoDelTerreno } from "./terrenoDeAntecedentes";

/** Lo que la pantalla puede pedirle a la pestaña desde el aviso de salida. */
export interface PestanaDeConstruccionesHandle {
  guardar: () => void;
  marcarGuardado: () => void;
}

interface PestanaDeConstruccionesProps {
  informe: Informe;
  /**
   * Lo que el encargado sincronizó desde la App de terreno. Solo se LEE: las
   * fotos, audios, textos y notas por dato proponen, no corrigen. Sin él (módulo
   * apagado, cargando o con error) la pestaña se dibuja sin ninguna pieza de
   * terreno y el cuadro es el de siempre.
   */
  terreno?: EstadoDelTerreno;
  /** Quién tiene asignada la visita: da el nombre cuando todavía no sincroniza. */
  asignado?: string | null;
  /** Abre la pestaña "Registro de terreno". */
  onVerTerreno?: () => void;
  /** El "Guardar cambios" de la pestaña está en vuelo. */
  guardando: boolean;
  /** Hay una operación de fila en vuelo (alta, corrección, borrado, foto). */
  ocupada: boolean;
  ref?: Ref<PestanaDeConstruccionesHandle>;
  onCambios?: (hayCambios: boolean) => void;
  /** Crea la construcción y, con el id que devuelve, sube sus fotos. */
  onAgregar: (campos: CamposDeLaFila, fotos: File[]) => void;
  onCorregir: (filaId: number, campos: CamposDeLaFila) => void;
  /** Corrige varias filas con los mismos campos, de a una (ver `useCuadroDelInforme`). */
  onCorregirVarias: (filaIds: number[], campos: CamposDeLaFila) => Promise<void>;
  onQuitarVarias: (filaIds: number[]) => Promise<void>;
  onSubirFoto: (filaId: number, archivo: File) => void;
  onQuitarFoto: (filaId: number, fotoId: number) => void;
  /** Guarda la leyenda libre de una foto de la fila (en el acto, fuera del formulario). */
  onLeyendaDeFoto?: (filaId: number, fotoId: number, leyenda: string | null) => void;
  onGuardar: (input: GuardarLaPestanaInput) => void;
}

/**
 * La pestaña "Construcciones": el cuadro de edificaciones del predio, su
 * descripción general y la casilla que declara que no hay ninguna.
 *
 * ES LA GEMELA DE "Maquinarias y equipos" CON EL MULTIPLICADOR CAMBIADO: allá
 * son UNIDADES y acá METROS CUADRADOS. Lo demás es igual —vida útil total y
 * residual, los dos valores unitarios, el ajuste comercial con signo— y por
 * eso el cuadro imprime UN total: el residual por la superficie con el ajuste
 * aplicado.
 *
 * TODO VA EN UF/m² y no hay selector de moneda: los rótulos lo dicen ("Valor
 * residual UF/m²"). La construcción se presupuesta así en Chile.
 *
 * EL CUADRO DEL PROTOTIPO SE CONTRADICE A SÍ MISMO EN DOS COSAS y acá manda el
 * FORMULARIO, que es donde están los rótulos y los ejemplos realistas:
 *
 * 1. La UNIDAD. El campo dice "UF/m2" con ejemplo 16,00 y el cuadro muestra
 *    95.000 en esa columna. A 95.000 UF/m² una casa de 180 m² valdría
 *    diecisiete millones de UF: esos números son pesos.
 * 2. El SIGNO del ajuste. El cuadro imprime "−8 %" y su total es el residual
 *    MÁS 8 % — medido en sus cuatro filas. El campo pide "Ej: 20,0", en
 *    positivo. Acá el porcentaje descuenta, que es la regla de maquinaria: dos
 *    pestañas hermanas no pueden leer al revés el mismo campo.
 *
 * DOS GUARDADOS, igual que sus dos hermanas: cada construcción se escribe en
 * el acto —desde el formulario en línea o desde su modal— y "Guardar cambios"
 * escribe lo que es de la PESTAÑA. POR ESO LAS FILAS NO CUENTAN COMO "CAMBIOS
 * SIN GUARDAR": cuando ese aviso aparece, ya están en el servidor.
 *
 * LO DE TERRENO SE CRUZA SIN ESCRIBIR NADA: las construcciones que el
 * encargado creó en la App y todavía no son fila aparecen en la lista como
 * TERRENO; guardarlas desde el modal crea la fila con su `terreno_ref`, y desde
 * ahí el registro cuelga de ella.
 */
export function PestanaDeConstrucciones({
  informe,
  terreno,
  asignado = null,
  onVerTerreno,
  guardando,
  ocupada,
  ref,
  onCambios,
  onAgregar,
  onCorregir,
  onCorregirVarias,
  onQuitarVarias,
  onSubirFoto,
  onQuitarFoto,
  onLeyendaDeFoto,
  onGuardar,
}: PestanaDeConstruccionesProps) {
  const filas = useMemo(() => informe.construcciones ?? [], [informe.construcciones]);
  const estado = informe.secciones?.construcciones;

  const [noAplica, setNoAplica] = useState(estado?.no_aplica ?? false);
  const [descripcion, setDescripcion] = useState(estado?.descripcion_general ?? "");
  const fuenteDelTexto = useFuenteDeLaDescripcion(descripcion);
  const [validada, setValidada] = useState(estado?.descripcion_general_validada ?? false);
  const [notas, setNotas] = useState(informe.notas?.construcciones ?? "");

  /* La referencia de "lo guardado" es estado y no las props: después de un
     guardado exitoso la base tiene que moverse a lo que quedó en pantalla. */
  const [base, setBase] = useState({
    noAplica: estado?.no_aplica ?? false,
    descripcion: estado?.descripcion_general ?? "",
    validada: estado?.descripcion_general_validada ?? false,
    notas: informe.notas?.construcciones ?? "",
  });

  const [propuestas, setPropuestas] = useState<ConstruccionPropuesta[]>([]);

  const [ejecucionPendiente, setEjecucionPendiente] = useState<number | null>(null);

  /*
   * UNA PROPUESTA PENDIENTE ES UN CAMBIO SIN GUARDAR, y una fila ya guardada
   * NO. La distinción es la de siempre —cuando el aviso aparecería, la fila ya
   * está en el servidor— pero lo que la IA propuso existe SOLO en el navegador:
   * cambiar de pestaña lo desmonta (Radix no conserva el contenido inactivo) y
   * se perdía sin que nada lo dijera.
   */
  const cambios =
    Number(noAplica !== base.noAplica) +
    Number(descripcion !== base.descripcion) +
    Number(validada !== base.validada) +
    Number(notas !== base.notas) +
    propuestas.length;
  const hayCambios = cambios > 0;

  useEffect(() => {
    onCambios?.(hayCambios);
  }, [hayCambios, onCambios]);

  useEffect(() => () => onCambios?.(false), [onCambios]);

  useImperativeHandle(ref, () => ({
    guardar,
    marcarGuardado: () => {
      // El panel se vacía porque lo propuesto ya se guardó: dejarlo lo
      // haría ver pendiente sobre filas que el cuadro ya muestra, y el
      // aviso de cambios sin guardar no se apagaría nunca.
      fuenteDelTexto.olvidar();
      setPropuestas([]);
      setAviso(null);

      setBase({ noAplica, descripcion, validada, notas });

      // GUARDAR LA PESTAÑA TAMBIÉN ACEPTA LA PROPUESTA aunque no se haya
      // agregado ninguna construcción: lo que se guardó pudo ser la
      // DESCRIPCIÓN.
      aceptarLaCorrida();
    },
  }));

  /* --- "Rellenar con IA" --- */

  const rellenar = useRellenarConstruccionesConIa(informe.id);
  const resolverElRelleno = useResolverElRelleno();

  const [aviso, setAviso] = useState<AvisoDelRelleno | null>(null);
  const [errorDelRelleno, setErrorDelRelleno] = useState<string | null>(null);

  /** Anota que la propuesta se usó, una sola vez. */
  function aceptarLaCorrida() {
    if (ejecucionPendiente === null) return;

    resolverElRelleno.mutate({ ejecucionId: ejecucionPendiente, resultado: "aceptado" });
    setEjecucionPendiente(null);
  }

  function pedirElRelleno() {
    setErrorDelRelleno(null);
    setAviso(null);

    rellenar.mutate(undefined, {
      onSuccess: ({ ejecucion_id, propuesta, documentos, pendientes, sin_usar }) => {
        setPropuestas(propuesta.construcciones);
        setEjecucionPendiente(ejecucion_id);

        // LA DESCRIPCIÓN SOLO SE ESCRIBE SI ESTÁ VACÍA, misma regla que en las
        // otras pestañas: un texto no se agrega, se pisa — y este lleva casilla
        // "Descripción validada". Se lee del ESTADO ACTUAL y no de una copia,
        // porque leer el expediente tarda y quien apretó sigue escribiendo.
        const propuso = propuesta.descripcion_general?.trim() ?? "";
        let escribio = false;

        // LA FUENTE DEL TEXTO SE MUESTRA SOLO MIENTRAS EL TEXTO SIGA SIENDO EL
        // PROPUESTO (el hook lo compara): si el campo ya tenía algo, la IA no
        // escribió y no hay fuente que mostrar. Un relleno nuevo reemplaza la
        // anterior.
        fuenteDelTexto.olvidar();
        if (propuso !== "") {
          fuenteDelTexto.registrar(propuso, propuesta.descripcion_general_fuente ?? null);
        }

        if (propuso !== "") {
          setDescripcion((actual) => {
            if (actual.trim() !== "") return actual;

            escribio = true;

            return propuso;
          });
        }

        setAviso({
          resumen: resumenDeLoPropuestoEnConstrucciones(
            propuesta.construcciones.length,
            escribio,
          ),
          documentos,
          pendientes,
          sinUsar: sin_usar,
        });
      },

      onError: (e) =>
        setErrorDelRelleno(
          getApiErrorMessage(
            e,
            "No pudimos leer el expediente. Inténtalo de nuevo o carga las construcciones a mano.",
          ),
        ),
    });
  }

  /** Descarta lo que queda de la propuesta. Lo ya agregado no se toca. */
  function descartarElRelleno() {
    if (ejecucionPendiente !== null) {
      resolverElRelleno.mutate({ ejecucionId: ejecucionPendiente, resultado: "descartado" });
      setEjecucionPendiente(null);
    }

    fuenteDelTexto.olvidar();
    setPropuestas([]);
    setAviso(null);
  }

  /* --- Lo de terreno y el modal de edición --- */

  const modo = modoDelTerreno(terreno);
  const registro = terreno?.estado === "ok" ? terreno.registro : undefined;
  const seccion = seccionDeConstrucciones(registro);
  const encargado = registro?.encargado?.nombre ?? asignado;

  const filasDelCuadroActual = useMemo(() => filasDelCuadro(filas, seccion), [filas, seccion]);

  /** Qué fila está abierta en el modal, por su clave (`web:<id>` o `terreno:<uuid>`). */
  const [abierta, setAbierta] = useState<string | null>(null);
  const enEdicion = filasDelCuadroActual.find((f) => f.clave === abierta) ?? null;

  /* UN solo visor para las fotos del modal y las de la descripción: recorre
     todas las de la sección, igual que en la pestaña de terreno. */
  const [fotoAbierta, setFotoAbierta] = useState<string | null>(null);
  const fotosDelRegistro = useMemo(
    () => (seccion ? fotosDelVisor([seccion], "todo") : []),
    [seccion],
  );

  function guardarDesdeElModal(fila: FilaDelCuadro, guardado: GuardadoDeLaConstruccion) {
    const campos = { ...camposDesdeConstruccion(guardado.formulario), validada: guardado.validada };

    if (fila.id !== null) {
      onCorregir(fila.id, campos);
    } else {
      // Una construcción de terreno sin guardar: el alta la ENLAZA con su
      // `terreno_ref`, y desde ahí sus registros cuelgan de la fila nueva.
      onAgregar({ ...campos, terreno_ref: fila.terrenoRef }, guardado.fotos);
    }

    setAbierta(null);
  }

  function guardar() {
    // LO PROPUESTO SE GUARDA ACÁ, no fila por fila. El porqué está en
    // `FilasPropuestas`: quien mira la pantalla va al botón grande, no a
    // los chicos de cada fila. Las que YA están no se repiten.
    propuestas
      .filter((construccion) => !yaEstaEnElCuadroDeConstrucciones(construccion.tipo_edificacion, filas))
      .forEach((construccion) =>
        onAgregar(camposDesdeConstruccion(formularioDesdeLaConstruccionPropuesta(construccion)), []),
      );

    onGuardar({
      no_aplica: noAplica,
      descripcion_general: descripcion.trim() === "" ? null : descripcion,
      descripcion_general_validada: validada,
      notas_observaciones: notas.trim() === "" ? null : notas,
    });
  }

  /** Vuelve la pestaña a lo último guardado y suelta lo que la IA propuso. */
  function descartarLosCambios() {
    setNoAplica(base.noAplica);
    setDescripcion(base.descripcion);
    setValidada(base.validada);
    setNotas(base.notas);
    descartarElRelleno();
  }

  const hayFilas = filas.length > 0;

  return (
    <div className="flex flex-col gap-3.5">
      {noAplica ? null : (
        <BandaDeRellenoConIa
          titulo="Cargar construcciones desde los documentos"
          rellenando={rellenar.isPending}
          // Con la casilla puesta no hay nada que rellenar: proponerle diez
          // construcciones a quien acaba de declarar que no hay ninguna sería
          // contradecirlo.
          disabled={guardando || ocupada || noAplica}
          onRellenar={pedirElRelleno}
        >
          La IA lee los documentos de Antecedentes legales y de Antecedentes históricos y propone
          las construcciones que encuentre.
          <br />
          Nada se guarda hasta que revises lo propuesto y aprietes «Guardar cambios».
        </BandaDeRellenoConIa>
      )}

      <AvisoDeLoRellenado
        rellenando={rellenar.isPending}
        error={errorDelRelleno}
        aviso={aviso}
        onDescartar={propuestas.length > 0 ? descartarElRelleno : null}
        // LO PROPUESTO SE GUARDA CON LA PESTAÑA, no fila por fila, así que el
        // aviso tiene que mandar al botón de abajo. Decía "cada una se guarda
        // al agregarla" y eso describía el modelo anterior: mandaba a apretar
        // un botón que ya no existe, y contradecía al panel que está justo
        // debajo. Es el mismo defecto que originó el cambio — el texto
        // llevando a la persona al lugar equivocado.
        queHacer={
          "Nada se guardó todavía. Revisa lo propuesto abajo y quita lo que no "
          + "corresponda; las construcciones que queden se guardan al apretar «Guardar "
          + "cambios», al pie de la pestaña."
        }
      />

      {noAplica ? null : (
        /* LAS COLUMNAS SE DECLARAN ACÁ: son lo único propio de esta pestaña. El
           panel aporta las decisiones —no esconder lo repetido, avisar que
           agregar guarda— que comparten las pestañas con cuadro. */
        <FilasPropuestas
          filas={propuestas}
          titulo={(cantidad) =>
            cantidad === 1
              ? "1 construcción propuesta por la IA"
              : `${cantidad} construcciones propuestas por la IA`
          }
          yaEsta={(c) => yaEstaEnElCuadroDeConstrucciones(c.tipo_edificacion, filas)}
          fuente={(fila) => fila.fuente}
          ocupada={ocupada}
          onQuitar={(c) => setPropuestas((actuales) => actuales.filter((otra) => otra !== c))}
          columnas={[
            { encabezado: "Tipo de edificación", celda: (c) => c.tipo_edificacion },
            { encabezado: "Grupo", celda: (c) => c.grupo ?? "—" },
            { encabezado: "Año", celda: (c) => c.anio ?? "—" },
            {
              encabezado: "Superficie m²",
              // `String()` porque los formateadores de `lib/montos` reciben el
              // string de un `decimal:*` de Eloquent, y una PROPUESTA trae
              // números: no hay columna que castear, es lo que respondió el
              // modelo.
              celda: (c) =>
                formatearCifra(c.superficie_m2 === null ? null : String(c.superficie_m2)),
              numerica: true,
            },
            { encabezado: "Clase", celda: (c) => c.clase ?? "—" },
            {
              // "UF/m²" y no "UF" a secas, igual que el cuadro de abajo: la
              // cifra es unitaria y al lado hay totales.
              encabezado: "Reposición UF/m²",
              celda: (c) => formatearMonto(c.valor_reposicion_uf_m2, null),
              numerica: true,
            },
            {
              encabezado: "Residual UF/m²",
              celda: (c) => formatearMonto(c.valor_residual_uf_m2, null),
              numerica: true,
            },
            {
              // CON SIGNO, igual que el cuadro: acá el porcentaje DESCUENTA,
              // así que un "8 %" sin signo se leería al revés.
              encabezado: "Ajuste",
              celda: (c) =>
                formatearAjuste(
                  c.ajuste_comercial_pct === null ? null : String(c.ajuste_comercial_pct),
                ),
              numerica: true,
            },
          ]}
        />
      )}

      <section className="bg-card flex flex-wrap items-center gap-3 rounded-xl border-[1.5px] px-4 py-3">
        <label
          title={hayFilas ? "Quita primero las construcciones de la lista" : undefined}
          className={cn(
            "flex w-fit items-center gap-2.5",
            hayFilas && !noAplica ? "cursor-not-allowed opacity-55" : "cursor-pointer",
          )}
        >
          <Checkbox
            checked={noAplica}
            // Con filas cargadas el registro se contradiría solo, y el servidor
            // lo rechaza con un 422.
            disabled={guardando || hayFilas}
            onCheckedChange={(v) => setNoAplica(v === true)}
          />
          <span className="text-[0.8125rem] font-bold">La propiedad no cuenta con construcciones</span>
        </label>
        {noAplica || hayFilas ? (
          <span className="text-ink-muted text-[0.71875rem] font-semibold">
            {noAplica
              ? "La sección se imprimirá indicando que no hay construcciones."
              : "Para marcarlo, quita antes las construcciones de la lista."}
          </span>
        ) : null}
      </section>

      {noAplica ? null : (
        <ListaDeConstrucciones
          filas={filasDelCuadroActual}
          modo={modo}
          encargado={encargado}
          ocupada={ocupada}
          onAgregar={onAgregar}
          onAbrir={(fila) => setAbierta(fila.clave)}
          // SE GUARDA SOLA y viaja sin el resto de los campos, para no pisar
          // lo que otro pudo haber corregido mientras tanto.
          onValidar={(fila, validadaAhora) => {
            if (fila.id !== null) onCorregir(fila.id, { validada: validadaAhora });
          }}
          onValidarVarias={(ids) => void onCorregirVarias(ids, { validada: true })}
          onQuitarVarias={(ids) => void onQuitarVarias(ids)}
        />
      )}

      <DescripcionDeLasConstrucciones
        informeId={informe.id}
        descripcion={descripcion}
        onDescripcion={setDescripcion}
        validada={validada}
        onValidada={setValidada}
        disabled={guardando}
        fuente={
          fuenteDelTexto.visible ? <FuenteDeLaPropuesta fuente={fuenteDelTexto.visible.fuente} /> : null
        }
        terreno={terreno}
        asignado={asignado}
        onVerTerreno={onVerTerreno}
        onAbrirFoto={setFotoAbierta}
        onRedactado={(texto) => {
          // Lo escribió un modelo: nadie lo revisó todavía, y la fuente que
          // declaró el relleno anterior ya no es la de este texto.
          fuenteDelTexto.olvidar();
          setDescripcion(texto);
          setValidada(false);
        }}
      />

      <section className="bg-card scroll-mt-5 rounded-xl border-[1.5px] p-5">
        <EncabezadoNumerado
          numero="03"
          idDelTitulo="construcciones-notas-titulo"
          titulo="Notas u observaciones"
          bajada="Opcional."
        />
        <div className="mt-3.5">
          <NotasDeLaPestana
            id="notas-construcciones"
            value={notas}
            onChange={setNotas}
            disabled={guardando}
            plana
          />
        </div>
      </section>

      {/* LA BARRA VA AL PIE de la pestaña, como en Antecedentes generales. Cuenta
          lo que es de la PESTAÑA (casilla, descripción, validación, notas y cada
          propuesta de la IA); las filas no entran porque ya están guardadas. */}
      <BarraDeGuardado
        cambios={cambios}
        enviando={guardando}
        etiquetaEnvio="Guardar cambios"
        ayuda="Las construcciones y sus fotos se guardan solas al agregarlas o editarlas."
        onGuardar={guardar}
        onDescartar={descartarLosCambios}
      />

      <DialogoDeConstruccion
        fila={enEdicion}
        informeId={informe.id}
        modo={modo}
        encargado={encargado}
        ocupada={ocupada}
        onGuardar={guardarDesdeElModal}
        onCerrar={() => setAbierta(null)}
        onSubirFoto={onSubirFoto}
        onQuitarFoto={onQuitarFoto}
        onLeyendaDeFoto={onLeyendaDeFoto}
        onAbrirFoto={setFotoAbierta}
      />

      {registro ? (
        <VisorDeFotos
          informeId={informe.id}
          fotos={fotosDelRegistro}
          abierta={fotoAbierta}
          onCambiar={setFotoAbierta}
        />
      ) : null}
    </div>
  );
}
